<!-- 
Dialog for configuring whether data comes from an automatic data source or from a manually uploaded spreadsheet.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>Configure data sources</v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="levy-collection-setup" section="configure-data-sources"/>
      </v-toolbar>
      <our-busy-indicator :show="busy"/>
      <v-card-text>
        <v-container grid-list-md pa-0>
          <v-layout wrap>
            <v-flex xs12 sm6>
              <v-select
                :value="configuration.useSpreadsheetForInScopeProviders"
                :items="inScopeProviderOptions"
                label="Data source for in-scope providers" 
                @keydown.enter.native.prevent
                @input="update('useSpreadsheetForInScopeProviders', $event)"
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-select
                :value="configuration.useSpreadsheetForLevyParameters"
                :items="levyParameterOptions"
                label="Data source for levy parameters" 
                @keydown.enter.native.prevent
                @input="update('useSpreadsheetForLevyParameters', $event)"
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-select
                :value="true"
                :items="[{text: 'Upload from spreadsheet', value: true}]"
                label="Data source for offline levy parameters" 
                hint="Offline levy parameters such as financial strength figures must be uploaded from a spreadsheet."
                @keydown.enter.native.prevent
              />
            </v-flex>
          </v-layout>
        </v-container>
        <our-required-field-indicator/>
      </v-card-text>
      <v-alert :value="showWarning" type="info">
        Please note: changing data sources will not change the underlying data
        until the import or spreadsheet upload task is next run.
      </v-alert>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
        <v-btn 
          ref="save-button" :disabled="busy" flat color="primary" 
          type="submit" @click.prevent="save"
        >Save</v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showValidationDialog" :state-error="state.error"/>
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false,
      showWarning: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.editDataSourceConfiguration;
    },
    configuration() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    levyParameterOptions() {
      const ds = this.configuration.type == "Vsl" ? "Import from VSL DataMart" : "Import from Unistats";
      return [{ text: ds, value: false }, { text: "Upload from spreadsheet", value: true }];
    },
    inScopeProviderOptions() {
      const ds = this.configuration.type == "Vsl" ? "Import from HITS & VSL DataMart" : "Import from HITS";
      return [{ text: ds, value: false }, { text: "Upload from spreadsheet", value: true }];
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.showWarning = false;
        this.$store.dispatch(action.EDIT_DATA_SOURCE_CONFIGURATION_GET, this.levyCollectionId);
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_SET_DATA, { [field]: newValue });
      this.showWarning = true;
    },
    async save() {
      if (await this.$store.dispatch(action.EDIT_DATA_SOURCE_CONFIGURATION_SAVE)) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
